import React, { useEffect, useState } from "react";
import { Button, Modal, Typography, App } from "antd";
import { ClearOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

import { useTranslation } from "react-i18next";

import { API_URL } from "../../utils/Util";
import { fetchT } from "../../utils/TypedUtil";
import { useAntdTheme } from "../../hooks";

const { Title } = Typography;

export type ItemAttributeGroup = {
  count: number;
  item_attributes: { id: string };
  oldest_pending_time: string;
  newest_pending_time: string;
};

export type ItemsByPriority = [number, Array<ItemAttributeGroup>];

export default ({
  itemsByPriorityArr,
  restaurantId,
  workstationID,
}: {
  itemsByPriorityArr: Array<ItemsByPriority>;
  restaurantId: string;
  workstationID: string;
}) => {
  const { t, i18n } = useTranslation();
  const { token } = useAntdTheme();
  const { message } = App.useApp();

  // Cutoff time - all tasks that are pending before this are cleared.
  const [endPendingTime, setEndPendingTime] = useState<string | null>(null);
  const [clearingInProgress, setClearingInProgress] = useState(false);

  const onCancel = () => setEndPendingTime(null);

  return (
    <>
      <Button
        danger
        size="large"
        icon={<ClearOutlined style={{ fontSize: 20 }} />}
        style={{
          height: token.controlHeightLG,
          minWidth: 120,
          borderRadius: token.borderRadiusLG,
        }}
        onClick={() => {
          const pendingTimes = itemsByPriorityArr.flatMap(([priority, itemGroups]) =>
            itemGroups.map((ig) => ig.newest_pending_time)
          );
          pendingTimes.sort().reverse();
          if (pendingTimes.length === 0) {
            return;
          }

          // Set the state so new tasks that arrive at the workstation after we
          // click the button are not automatically included in this clear
          // attempt.
          setEndPendingTime(pendingTimes[0]);
        }}
      />
      <Modal
        title={
          <Title level={4} style={{ margin: 0 }}>
            {t("clear_all_items")}
          </Title>
        }
        open={endPendingTime != null}
        onCancel={onCancel}
        styles={{
          body: { padding: token.paddingXL },
        }}
        footer={[
          <Button
            key="cancel"
            onClick={onCancel}
            size="large"
            style={{
              borderRadius: token.borderRadiusLG,
              height: token.controlHeightLG,
            }}
          >
            {t("cancel")}
          </Button>,
          <Button
            key="clear"
            type="primary"
            size="large"
            disabled={endPendingTime == null}
            loading={clearingInProgress}
            style={{
              borderRadius: token.borderRadiusLG,
              height: token.controlHeightLG,
            }}
            onClick={() => {
              setClearingInProgress(true);

              fetchT<{}>(
                "POST",
                `${API_URL}/v1/r/${restaurantId}/workstations/${workstationID}/clear`,
                {
                  end_pending_time: endPendingTime,
                },
                () => {
                  setClearingInProgress(false);
                  setEndPendingTime(null);
                },
                (err) => {
                  // TODO(kku): using message instead of notification for now
                  // since we make a separate call for each item, which
                  // results in a lot of notifications.
                  message.error(t("error_occurred") + "，" + t("please_try_again_later"));
                  setClearingInProgress(false);
                  setEndPendingTime(null);
                }
              );
            }}
          >
            {t("clear")}
          </Button>,
        ]}
      >
        <div style={{ fontSize: 18 }}>
          <ExclamationCircleOutlined
            style={{
              marginRight: 8,
              color: token.colorWarning,
            }}
          />
          {t("clear_all_items")}
        </div>
      </Modal>
    </>
  );
};
